import { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { Box } from 'components/Box/Box';
import { colors, transition } from 'theme/theme';

import { CustomTooltip, aChangeColor, aGrow, easeOut } from './Bar.styled';

type BarProps = {
  $animationDelay?: number;
  className?: string;
  component?: ReactNode;
  index: number;
  initialized: boolean;
  bucketCount: number;
  offset?: number;
  tooltip: ReactNode;
  height?: string;
  tooltipColor?: string;
  tooltipOffset?: number;
  showTooltip?: boolean;
  width: string;
  orientation?: 'vertical' | 'horizontal';
};

const INTERVAL = 100;

export const Bar = styled(
  ({
    className,
    component,
    initialized,
    height,
    offset = 0,
    orientation = 'vertical',
    tooltipColor = colors.brandGreen,
    tooltip,
    showTooltip = false,
    width,
  }: BarProps) => (
    <>
      <Box height={height} width={width} className={className}>
        {component || (
          <Box component="svg" viewBox="0 0 1 1" preserveAspectRatio="none">
            <rect
              width="1"
              height="1"
              fill={!initialized ? colors.selectionGrey : colors.brandGreen}
            />
          </Box>
        )}
      </Box>
      <Box
        position="absolute"
        bottom={`${orientation === 'vertical' ? offset : 0}%`}
        left={`${orientation === 'horizontal' ? offset : 0}%`}
        width={width || 1}
        maxWidth={orientation === 'vertical' ? '60px' : 'none'}
        height={height}
        pointerEvents="none"
      >
        {tooltip && showTooltip ? (
          <CustomTooltip $color={tooltipColor}>{tooltip}</CustomTooltip>
        ) : null}
      </Box>
    </>
  ),
)<{
  $active: boolean;
  $animationDelay: number;
  bucketCount: number;
  index: number;
  $loading: boolean;
  orientation?: 'vertical' | 'horizontal';
  offset: number;
  tooltipOffset?: number;
  width: string;
}>`
  position: absolute;
  bottom: ${({ offset, orientation = 'vertical' }) =>
    orientation === 'vertical' ? `${offset}%` : 0};
  left: ${({ offset, orientation = 'vertical' }) =>
    orientation === 'horizontal' ? `${offset}%` : '0'};
  transform: scaleY(0) translateZ(0);
  transform-origin: center bottom;
  max-width: ${({ orientation = 'vertical' }) =>
    orientation === 'vertical' ? '60px' : 'none'};
  width: ${(props) => props.width};
  background: ${(props) => {
    if (!props.initialized || props.$loading) {
      return colors.elementGrey;
    }

    if (!props.$active) {
      return colors.selectionGrey;
    }

    return colors.brandGreen;
  }};
  transition:
    background ${transition},
    height 1s ${easeOut};
  animation-name: ${aGrow};
  animation-duration: ${(props) =>
    (props.bucketCount - props.index) * INTERVAL}ms;
  animation-delay: ${(props) =>
    (props.index * INTERVAL) / 2 + props.$animationDelay || 0}ms;
  animation-timing-function: ${easeOut};
  animation-fill-mode: forwards;
  will-change: background, height, transform;
  z-index: ${(props) =>
    !props.initialized || props.$loading || props.$active ? 101 : 1};

  ${(props) =>
    props.$loading
      ? css`
          background: ${colors.elementGrey};
          transform: scaleY(1) translateZ(0);
          transition: none;
          animation-name: ${aChangeColor(props.bucketCount)};
          animation-duration: ${props.bucketCount * 200}ms;
          animation-delay: ${props.index * 200}ms;
          animation-iteration-count: infinite;
          z-index: 102;
        `
      : undefined}

  ${(props) =>
    props.initialized && props.$active
      ? `
        cursor: pointer;

        &:hover {
          background: ${colors.tagLemonGreen};
          & + * > ${CustomTooltip} {
            transform: translateY(0) translateX(-50%);
            opacity: 1;
            visibility: visible;
          }
        }
      `
      : undefined}

  & + * > ${CustomTooltip} {
    position: absolute;
    z-index: 104;
    left: ${(props) => props.tooltipOffset || 50}%;
    bottom: calc(100% + 18px);
    transform: translateY(4px) translateX(-50%);
    opacity: 0;
    visibility: hidden;
    transition: all ${transition};
    will-change: translate, opacity;
  }

  svg {
    display: none;
    width: 100%;
    height: 100%;

    @media print {
      display: block;
    }
  }

  ${(props) =>
    props.component
      ? `
        background: transparent;

        &:hover {
          background: transparent;
        }

        svg {
          display: block;
        }
      `
      : undefined}
`;
